import { Text } from "components/shared/text";
import React from "react";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";

import {
  getApplicationByCode,
  getSubApplicationByCode,
  getSystemByCode,
} from "components/EtudeForm/constants";
import { InstallationType } from "components/EtudeForm/hooks/useGetSolutionsQuery";
import { SavePopup } from "components/popups/SavePopup";
import Button from "components/shared/button";
import { usePopup } from "providers/PopupProvider";
import useTranslation from "utils/hooks/useTranslation";
import { getSolutionID, getSolutionNomDeProposition } from "./../helpers";
import { useAuth } from "utils/hooks/useAuth";
import { EtudeStep } from "utils/defines";
import { SvgMarker } from "components/svg";

interface FormatOptions {
  tolerance?: number;
  precision?: number;
  forcePrecision?: boolean;
}

export const EtudeInfoTable: React.FC<{
  selectionInfo: {
    selectedAccessoiresNonSupp: Record<string, boolean>;
    selectedAccessoiresSupp: Record<string, number | undefined>;
    selectedBoilers: Record<string, boolean>;
    selectedServices: Record<string, boolean>;
  };
  json: {
    application: string;
    subApplication: string;
    systeme: string;
    etudeFormSolution?: EtudeSolution;
    TcoupureQuestion?: boolean;
    Tcoupure?: number | null;
  };
  service: string;
  installationType: InstallationType;
  etudeData: GetEtudeResponse;
  hasEcs: boolean;
  solution: Solution;
}> = ({
  selectionInfo,
  json,
  solution,
  installationType,
  service,
  hasEcs,
  etudeData,
}) => {
    const { t } = useTranslation();
    const { network } = useSalesNetwork();
    const { openPopup, closeLastPopup } = usePopup();
    const { isAuth } = useAuth();

    const {
      selectedAccessoiresNonSupp,
      selectedAccessoiresSupp,
      selectedBoilers,
      selectedServices,
    } = selectionInfo;

    const { application, subApplication, systeme, etudeFormSolution, TcoupureQuestion, Tcoupure: tcoupureValue } = (json ||
      {}) as {
        application: string;
        subApplication: string;
        systeme: string;
        etudeFormSolution?: EtudeSolution;
        TcoupureQuestion?: boolean;
        Tcoupure?: number | null;
      };

    const hasTcoupure = !!(TcoupureQuestion && tcoupureValue != null);

    const formatPuissance = (
      value?: number,
      options: FormatOptions = {}
    ): string => {
      const { tolerance = 0.05, precision = 1, forcePrecision = false } = options;

      if (value === undefined || value === null) return "";

      const rounded = Math.round(value);
      const decimalDiff = Math.abs(value - rounded);

      if (!forcePrecision && decimalDiff <= tolerance) {
        return rounded.toString();
      }

      // Handle edge case where value is very close to zero
      if (Math.abs(value) < Math.pow(10, -precision)) {
        return "0";
      }

      // Format with precision, then trim trailing zeros & decimal point if not needed
      return parseFloat(value.toFixed(precision)).toString();
    };

    const { siteName, clientName, version, numero } = etudeData || {};
    const { part_pac, part_pac_t_coupure, pac, taux_couverture } = solution || {};
    const { puissance_a_t_base, puissance_a_t_coupure } = pac || {};
    const tableInfo = [
      { label: "FO_DOC_PROJECT_NAME", value: siteName },
      { label: "FO_DOC_CLIENT_NAME", value: clientName },
      { label: "FO_DOC_ID", value: numero },
      { label: "FO_DOC_VERSION", value: version },
      {
        label: "FO_DOC_INSTAL_TYPE",
        value: t(
          installationType == InstallationType.electric
            ? "FO_SIMULATION_DES_PAC"
            : "FO_SIMULATION_DES_HYBRID"
        ),
      },
      {
        label: "FO_DOC_HP",
        value: solution ? getSolutionNomDeProposition(solution) : "",
      },
      {
        label: hasTcoupure ? "FO_RESULT_kW_HP_@T°C_COUPURE_TOTAL" : "FO_RESULT_%_HP_@T°C_BASIS",
        value: (hasTcoupure ? part_pac_t_coupure : part_pac) != null
          ? `${Math.round((hasTcoupure ? (part_pac_t_coupure ?? part_pac) : part_pac) * 100)} ${t("FO_UNIT_PERCENT")}`
          : "",
      },
      {
        label: hasTcoupure ? "FO_RESULT_kW_HP_@T°C_COUPURE" : "FO_RESULT_kW_HP_@T°C_BASIS",
        value: (hasTcoupure ? puissance_a_t_coupure : puissance_a_t_base) != null
          ? `${formatPuissance(hasTcoupure ? (pac?.puissance_a_t_coupure ?? pac?.puissance_a_t_base) : pac?.puissance_a_t_base)} ${t("FO_UNIT_KW")}`
          : "",
      },
    ];

    if (hasEcs) {
      tableInfo.push(
        {
          label: "FO_DOC_APP_SUBAPP",
          value:
            network && service
              ? `${t(
                getApplicationByCode(application, service, network)?.label ?? ""
              )} - ${t(
                getSubApplicationByCode(
                  subApplication,
                  application,
                  service,
                  network
                )?.label ?? ""
              )}`
              : "",
        },
        {
          label: "FO_DOC_PROD_DHW",
          value:
            network && service
              ? t(
                getSystemByCode(
                  systeme,
                  subApplication,
                  application,
                  service,
                  network
                )?.label ?? ""
              )
              : "",
        },
        {
          label: "FO_DOC_SOL_DHW",
          value: etudeFormSolution?.references_commerciales
            ?.map((rc) => rc.label)
            .join(" - "),
        }
      );
    } else {
      tableInfo.push({
        label: "FO_RESULT_%_ANNUAL_COVERAGE",
        value: taux_couverture
          ? `${Math.round(taux_couverture * 100)} ${t("FO_UNIT_PERCENT")}`
          : "",
      });
    }
    return (
      <group data-direction="column" data-length="autofit" data-gap="24">
        <Text
          data-weight="600"
          data-ellipsis=""
          data-color="grey-color-dark"

        //data-wrap="wrap"
        >
          FO_DOC_GLOBAL_INFO
        </Text>
        <group
          data-direction="column"
          data-radius="5"
          data-contain=""
          data-border=""
        >
          {tableInfo.map(({ label, value }) => (
            <group key={label} data-wrap="no" data-border="">
              <group
                data-length="200"
                data-min-height="40"
                data-space-horizontal="10"
                data-align="center"
                data-fit="2"
                data-background="main"
                data-color="white"
              >
                <Text
                  data-position="left"
                  data-weight="600"
                  data-ellipsis=""
                  data-wrap="wrap"
                >
                  {label}
                </Text>
              </group>
              <group data-space-horizontal="10" data-align="center" data-fit="1">
                <text
                  data-length="320"
                  data-haight="auto"
                  data-position="left"
                  data-color="grey-color-dark"
                  data-weight="600"
                  data-wrap="wrap"
                >
                  {value?.toString() || "-"}
                </text>
              </group>
            </group>
          ))}
        </group>
        <Button
          primary
          data-length="auto"
          data-gap="10"
          data-space="20"
          data-justify="left"
          disabled={!isAuth}
          onClick={() => {
            openPopup(
              <SavePopup
                closePopup={() => {
                  closeLastPopup();
                }}
                application={application as any}
                subApplication={subApplication as any}
                systeme={systeme}
                formData={{
                  ...json,
                  accessoires: {
                    selectedAccessoiresNonSupp,
                    selectedServices,
                    selectedAccessoiresSupp,
                    selectedBoilers: Object.fromEntries(
                      Object.entries(selectedBoilers).filter(([, v]) => v)
                    ),
                  },
                  selectedSolution: solution,
                  selectedSolutionID: getSolutionID(solution!),
                  step: EtudeStep.Documents,
                }}
              ></SavePopup>
            );
          }}
        >
          <icon data-svg-size="16">
            <SvgMarker />
          </icon>
          <Text data-text-size="large" data-text-align="left" data-weight="400">
            FO_DOCUMENT_ADMIN_MODIFY
          </Text>
        </Button>
      </group>
    );
  };
